// @noflow
import React from 'react'

import useWindowSize from '@/hooks/useWindowSize'

import { Button } from '@/components/elements/atoms/Button/Button'
import Image from '@/components/elements/atoms/Image/Image'
import Link from '@/components/elements/atoms/Link/Link'
import Modal from '@/components/elements/atoms/Modal/Modal'
import Text, { Props as TextProps } from '@/components/elements/atoms/Text/Text'
import { BREAKPOINTS } from '@/components/templates/Base'

import STYLES from './CountdownModal.module.sass'

import Countdown from '../Countdown/Countdown'

type Props = {
  isModalOpen: boolean
  toggleModal: (isOpen: boolean) => void
  heroImageSlug: string
  heroImageAlt: string
  countdownEndDate?: string | Date
  headingText: TextProps
  descriptionText: TextProps
  buttonText: TextProps
  buttonOnClick: () => void
  footerText?: TextProps
  footerLink?: string
  loading?: boolean
  children?: React.ReactNode
}

const CountdownModal = ({
  isModalOpen,
  toggleModal,
  heroImageSlug,
  heroImageAlt,
  countdownEndDate,
  headingText,
  descriptionText,
  buttonText,
  footerText,
  footerLink,
  buttonOnClick,
  loading = false,
  children
}: Props): JSX.Element | null => {
  const { windowWidth } = useWindowSize()

  const isMobile = windowWidth < BREAKPOINTS.md
  const copyContext = 'countdown_modal'
  const namespace = 'molecules'

  return (
    <Modal
      setOpenModal={toggleModal}
      isModalOpen={isModalOpen}
      width={450}
      bottomSticky={isMobile}
      padding={false}
    >
      <div className={STYLES.container}>
        <div className={STYLES.imageWrapper}>
          <Image
            slug={heroImageSlug}
            image={{
              width: 700,
              height: 394,
              resizeMode: 'resize_to_fill',
              quality: 'auto:best'
            }}
            alt={heroImageAlt}
          />
        </div>
        <div className={STYLES.countdownBanner}>
          <Text
            text={`${copyContext}.countdown_text`}
            namespace={namespace}
            variant="display14"
            margin={false}
          />
          <Countdown
            countdownType="dailyCountdown"
            targetDate={countdownEndDate}
            labels={{
              daysLabel: `${copyContext}.days`,
              hoursLabel: `${copyContext}.hours`,
              minutesLabel: `${copyContext}.minutes`,
              secondsLabel: `${copyContext}.seconds`
            }}
            namespace={namespace}
            textColour="brandBlue500"
            variant="oneLine"
          />
        </div>
        <div className={STYLES.content}>
          <Text
            variant="display24"
            align="center"
            margin={false}
            {...headingText}
          />
          <div className={STYLES.description}>
            <Text
              variant="textRegular12"
              align="center"
              margin={false}
              {...descriptionText}
            />
          </div>
          {children}
          <div className={STYLES.buttonWrapper}>
            <Button
              typography={buttonText}
              identifier="raf-campaign-modal-button"
              variant="primary"
              screenIdentifier="dashboard"
              onClick={buttonOnClick}
              fullWidth
              disabled={loading}
            />
          </div>
          {footerText ? (
            footerLink ? (
              <Link
                text={footerText.text}
                href={footerLink}
                disableAnalytics
                opensInNewTab
                suffix={null}
                translate={false}
              />
            ) : (
              <Text
                variant="textRegular12"
                align="center"
                margin={false}
                {...footerText}
              />
            )
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export default CountdownModal
